import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
  setSessionExpiryHandler as _setSessionExpiryHandler,
  resetSessionExpiryLatch as _resetSessionExpiryLatch,
  triggerSessionExpired as _triggerSessionExpired,
} from './sessionExpiryLatch';
import { shouldAttemptSessionRefresh, shouldTriggerSessionExpiry } from './sessionExpiryPolicy';

// Re-export the pure latch API from AuthContext so existing importers
// (App.js SessionExpiryBridge, tests) keep working unchanged.
export const setSessionExpiryHandler = _setSessionExpiryHandler;
export const resetSessionExpiryLatch = _resetSessionExpiryLatch;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * SESSION-ONLY AUTH STORAGE
 * ------------------------------------------------------------------
 * Access token is kept in sessionStorage (NOT localStorage) so that
 * closing the browser/tab automatically logs the user out — they must
 * re-enter their password to sign back in.
 *
 * - sessionStorage is scoped to the tab and erased on tab close.
 * - The backend's auth/refresh/csrf cookies are also session cookies
 *   (no max-age), so the cookie copy disappears at the same moment.
 *
 * On first load after this migration we proactively wipe any legacy
 * `access_token` left behind in localStorage so old persisted sessions
 * are revoked client-side too.
 */
const AUTH_TOKEN_KEY = 'access_token';
const authStorage = {
  get: () => {
    try { return sessionStorage.getItem(AUTH_TOKEN_KEY); } catch { return null; }
  },
  set: (v) => {
    try { sessionStorage.setItem(AUTH_TOKEN_KEY, v); } catch { /* quota / private mode */ }
  },
  remove: () => {
    try { sessionStorage.removeItem(AUTH_TOKEN_KEY); } catch { /* noop */ }
  },
};

// One-time migration: clear any persisted token from the previous
// localStorage-backed implementation so users who had a long-lived
// session don't keep it forever after the upgrade.
(function purgeLegacyPersistedToken() {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(AUTH_TOKEN_KEY)) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
  } catch { /* noop */ }
})();

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Bir hata oluştu. Lütfen tekrar deneyin.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Read CSRF cookie (set by backend on login/refresh) and echo as header so the
// backend's double-submit CSRF middleware accepts the request.
//
// FALLBACK RATIONALE: In some browser contexts (e.g. an embedded preview
// iframe / CHIPS-partitioned cookie jars) the non-HttpOnly `csrf_token`
// cookie returned by /api/auth/login and /api/quote/auth/register is
// dropped even though the HttpOnly auth cookies are kept. To survive that,
// callers ALSO stash the token in sessionStorage via `persistCsrfToken()`;
// this reader falls back to sessionStorage if `document.cookie` is empty,
// so the same-origin double-submit check still passes. `logout()` and any
// forced re-login wipe the fallback so it never outlives the JWT.
export const CSRF_STORAGE_KEY = 'csrf_token';
function getCsrfCookie() {
  if (typeof document !== 'undefined') {
    const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
    if (match) return decodeURIComponent(match[1]);
  }
  try {
    return sessionStorage.getItem(CSRF_STORAGE_KEY) || '';
  } catch { return ''; }
}

// Persist CSRF token in BOTH document.cookie (primary) and sessionStorage
// (fallback). Called after successful login/register when the backend
// returns `csrf_token` in the JSON body.
export function persistCsrfToken(token) {
  if (!token) return;
  try {
    // Write to document.cookie so same-origin JS reads it back. `Secure`
    // is required because the app is served over HTTPS; `SameSite=Lax`
    // matches the backend's own set_csrf_cookie policy.
    document.cookie = `csrf_token=${encodeURIComponent(token)}; path=/; SameSite=Lax; Secure`;
  } catch { /* noop */ }
  try { sessionStorage.setItem(CSRF_STORAGE_KEY, token); } catch { /* noop */ }
}

function clearCsrfToken() {
  try {
    document.cookie = 'csrf_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure';
  } catch { /* noop */ }
  try { sessionStorage.removeItem(CSRF_STORAGE_KEY); } catch { /* noop */ }
}

// Refresh uses a dedicated Axios client so a failed refresh request never
// re-enters the global 401 interceptor and creates a retry loop.
const refreshClient = axios.create({ withCredentials: true });
let refreshPromise = null;

function setAxiosHeader(config, name, value) {
  if (!config.headers) config.headers = {};
  if (typeof config.headers.set === 'function') config.headers.set(name, value);
  else config.headers[name] = value;
}

async function refreshAuthenticatedSession() {
  if (refreshPromise) return refreshPromise;
  const API_URL = process.env.REACT_APP_API_URL;
  refreshPromise = refreshClient.post(`${API_URL}/api/auth/refresh`, {})
    .then(({ data }) => {
      if (!data?.access_token) throw new Error('Refresh response missing access token');
      authStorage.set(data.access_token);
      if (data.csrf_token) persistCsrfToken(data.csrf_token);
      return data.access_token;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

// Setup axios interceptor to attach token to every request
const setupAxiosInterceptor = () => {
  axios.interceptors.request.use((config) => {
    const token = authStorage.get();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // CSRF token: required on state-changing requests (POST/PUT/PATCH/DELETE)
    const method = (config.method || 'get').toLowerCase();
    if (!['get', 'head', 'options'].includes(method)) {
      const csrf = getCsrfCookie();
      if (csrf) {
        config.headers['X-CSRF-Token'] = csrf;
      }
    }
    config.withCredentials = true;
    return config;
  });

  // Authenticated 401 responses first get one transparent refresh attempt.
  // Only a failed refresh/retry reaches the existing session-expired flow.
  axios.interceptors.response.use(
    (r) => r,
    async (err) => {
      if (shouldAttemptSessionRefresh(err)) {
        const original = err.config;
        original.__paydaaiRefreshAttempted = true;
        try {
          const accessToken = await refreshAuthenticatedSession();
          setAxiosHeader(original, 'Authorization', `Bearer ${accessToken}`);
          const method = (original.method || 'get').toLowerCase();
          if (!['get', 'head', 'options'].includes(method)) {
            const csrf = getCsrfCookie();
            if (csrf) setAxiosHeader(original, 'X-CSRF-Token', csrf);
          }
          original.withCredentials = true;
          return axios(original);
        } catch {
          // Refresh cookie is missing/expired/revoked: expire the visible session below.
        }
      }
      if (shouldTriggerSessionExpiry(err)) {
        triggerSessionExpired(err?.config?.url || '');
      }
      return Promise.reject(err);
    },
  );
};

// -------- Session-expiry singleton --------
// The latch state machine lives in `./sessionExpiryLatch.js`. The
// interceptor below calls `triggerSessionExpired` on any 401 that
// carried an auth header; the latch guarantees at-most-one handler
// call per session, and successful login/register re-arm it.
function triggerSessionExpired(url) {
  _triggerSessionExpired(url, () => {
    // Nuke local auth state IMMEDIATELY so no further requests attach
    // the expired token.
    authStorage.remove();
    clearCsrfToken();
  });
}

// Test-only: allow the auth test suite to drive the 401 flow directly
// without spinning up axios + a mocked server.
export function __testOnly_triggerSessionExpired(url) {
  triggerSessionExpired(url);
}

setupAxiosInterceptor();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = authStorage.get();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      
      const defaultLabels = { people: 'Kullanıcılar', payment: 'Ödeme', identifier: 'Numara' };
      
      const mergeLabels = (fetchedLabels) => ({
        people: fetchedLabels?.people || defaultLabels.people,
        payment: fetchedLabels?.payment || defaultLabels.payment,
        identifier: fetchedLabels?.identifier || defaultLabels.identifier,
      });

      if (data.role === 'superadmin') {
        data.labels = defaultLabels;
        data.onboarded = true;
        setUser(data);
        return;
      }

      try {
        const onboardingRes = await axios.get(`${API_URL}/api/onboarding/status`);
        data.onboarded = (data.role === 'admin' || data.role === 'staff') ? true : onboardingRes.data.onboarded;
        data.business_type = onboardingRes.data.business_type;
        data.business_name = onboardingRes.data.business_name;
        data.business_id = onboardingRes.data.business_id;
        // Carried so utils/sectorTerminology can pick the right Türkçe term
        // set (Hastalar/Müşteriler/Sakinler/Mükellefler/...).
        data.appointment_category = onboardingRes.data.appointment_category || null;
        data.appointment_subcategory = onboardingRes.data.appointment_subcategory || null;
        data.labels = mergeLabels(onboardingRes.data.labels);
        data.package = onboardingRes.data.package || null;
        data.is_frozen = onboardingRes.data.is_frozen || false;
        data.billing_complete = onboardingRes.data.billing_complete || false;
        data.billing_info = onboardingRes.data.billing_info || null;
        data.trial_status = onboardingRes.data.trial_status || null;
        data.business_frozen = onboardingRes.data.is_frozen || false;
        data.plan = onboardingRes.data.plan || 'randevu';
        data.allow_branches = onboardingRes.data.allow_branches === true;
      } catch (err) {
        const errDetail = err.response?.data?.detail || '';
        if (errDetail.startsWith('FROZEN:')) {
          data.is_frozen = true;
          data.onboarded = true;
          data.labels = defaultLabels;
        } else {
          data.onboarded = (data.role === 'admin' || data.role === 'staff') ? true : false;
          data.labels = defaultLabels;
          data.is_frozen = false;
        }
      }
      
      setUser(data);

      // Sync i18n to the user's stored Quote Builder language preference
      // (`quote_profiles.language`). We fetch the profile only when the
      // Quote Builder module is enabled — legacy tenants don't have a
      // profile and would just add a wasted 404. Priority: URL `?lang=`
      // is applied at i18n boot; if a `paydaai_lang_pending_v1` marker was
      // set from the URL we PUSH it to the profile once, then remove the
      // marker so it doesn't flip the profile again on later reloads.
      try {
        const i18n = (await import('@/i18n')).default;
        let lang = null;
        const quoteEnabled = process.env.REACT_APP_QUOTE_MODULE_ENABLED === 'true';
        const quoteLocales = ['tr', 'en', 'de', 'fr', 'es'];
        let pending = null;
        try { pending = sessionStorage.getItem('paydaai_lang_pending_v1'); } catch { /* noop */ }
        if (quoteEnabled) {
          if (pending && quoteLocales.includes(pending)) {
            try {
              const { data: prof } = await axios.put(`${API_URL}/api/quote/profile`, { language: pending });
              lang = (prof && typeof prof.language === 'string') ? prof.language.toLowerCase() : pending;
            } catch { lang = pending; }
            try { sessionStorage.removeItem('paydaai_lang_pending_v1'); } catch { /* noop */ }
          } else {
            try {
              const { data: prof } = await axios.get(`${API_URL}/api/quote/profile`, { params: { include_logo: false } });
              lang = (prof && typeof prof.language === 'string') ? prof.language.toLowerCase() : null;
            } catch { /* no quote profile — fall through */ }
          }
        }
        if (!lang) lang = data.language;
        if (lang && quoteLocales.includes(lang) && i18n.resolvedLanguage !== lang) {
          await i18n.changeLanguage(lang);
        }
        if (lang && quoteLocales.includes(lang)) {
          try { localStorage.setItem('paydaai_lang_v1', lang); } catch { /* noop */ }
        }
      } catch { /* i18n bootstrap not ready — first paint will use localStorage */ }
    } catch (error) {
      const errDetail = error.response?.data?.detail || '';
      if (errDetail.startsWith('FROZEN:')) {
        setUser({ is_frozen: true, role: 'admin' });
      } else {
        authStorage.remove();
        clearCsrfToken();
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      // SECURITY: Pre-clear all client-side state so an old higher-privileged
      // cookie cannot leak into a new (lower-privileged) login attempt.
      authStorage.remove();
      setUser(null);

      const loginRes = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password }
      );

      // Save token to sessionStorage (auto-wiped on tab close)
      if (loginRes.data.access_token) {
        authStorage.set(loginRes.data.access_token);
      }
      // Also persist CSRF token from JSON body (belt-and-suspenders for
      // browser contexts where the Set-Cookie is dropped, e.g. embedded
      // iframe / partitioned cookie jars).
      if (loginRes.data.csrf_token) {
        persistCsrfToken(loginRes.data.csrf_token);
      }

      const role = loginRes.data.role;

      // SECURITY: The /api/auth/login endpoint never returns superadmin role
      // (it's hard-blocked server-side). If we somehow receive it, treat as
      // a tampering attempt and refuse to proceed.
      if (role === 'superadmin') {
        await axios.post(`${API_URL}/api/auth/logout`, {}).catch(() => {});
        authStorage.remove();
        clearCsrfToken();
        setUser(false);
        return { success: false, error: 'Geçersiz oturum türü.' };
      }

      localStorage.removeItem('selectedBusinessType');

      await checkAuth();
      // A3: after a successful login the 401 latch must reset so a
      // future session expiry can fire again on this SPA session.
      resetSessionExpiryLatch();
      return { success: true, role };
    } catch (error) {
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: errorMessage };
    }
  };

  const register = async (payload) => {
    try {
      await axios.post(
        `${API_URL}/api/auth/register`,
        payload
      );
      await checkAuth();
      // A3: same latch reset — a successful register mints a fresh
      // session, so future 401s must be handled again.
      resetSessionExpiryLatch();
      return { success: true };
    } catch (error) {
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (error) {
      // silent
    } finally {
      authStorage.remove();
      clearCsrfToken();
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
